import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Users, Receipt, LogOut, User, Settings } from 'lucide-react';

interface NavigationProps {
  currentView: 'employee' | 'admin';
  onViewChange: (view: 'employee' | 'admin') => void;
  userRole: string | null;
}

export function Navigation({ currentView, onViewChange, userRole }: NavigationProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const canAccessAdmin = userRole === 'admin' || userRole === 'manager' || userRole === 'bookkeeper';
  const displayName = user?.displayName || user?.email;

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
      <div className="bg-background/80 backdrop-blur-md border border-border rounded-full p-1 shadow-medium">
        <div className="flex items-center gap-1">
          <Button
            variant={currentView === 'employee' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onViewChange('employee')}
            className="rounded-full gap-2"
          >
            <Receipt className="h-4 w-4" />
            Employee View
          </Button>

          {canAccessAdmin && (
            <Button
              variant={currentView === 'admin' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onViewChange('admin')}
              className="rounded-full gap-2"
            >
              <Users className="h-4 w-4" />
              Admin View
            </Button>
          )}

          <div className="flex items-center gap-2 ml-2 pl-2 border-l border-border">
            <Badge variant="secondary" className="text-xs">
              {userRole}
            </Badge>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-full p-2">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback>
                      {user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled>
                  <User className="h-4 w-4 mr-2" />
                  {displayName}
                </DropdownMenuItem>
                {userRole === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/users')}>
                    <Settings className="h-4 w-4 mr-2" />
                    User Management
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
}
